import Link from "next/link";
import {
  Clock,
  Layers,
  Laptop,
  Star,
  Users,
  ArrowRight,
  Code2,
  Palette,
  Megaphone,
  Briefcase,
  Sprout,
  Crown,
  Plane,
  Cpu,
  Smile,
  Languages,
  type LucideIcon,
} from "lucide-react";
import type { Course } from "@/lib/types";
import { PAYMENT_NOTE } from "@/lib/utils";
import { courseImageFor, hasUploadedCourseImage } from "@/lib/images";
import { Cover } from "@/components/ui/Cover";
import { RevealItem } from "@/components/ui/Reveal";
import { EnrollButton } from "@/components/sections/EnrollButton";

export const categoryIcon: Record<string, LucideIcon> = {
  Technology: Code2,
  Design: Palette,
  Marketing: Megaphone,
  Business: Briefcase,
  Leadership: Crown,
  "Personal Growth": Sprout,
  "Travel & Tourism": Plane,
  "IT Courses": Cpu,
  "Personality Development": Smile,
  IELTS: Languages,
};

export function CourseCard({ course }: { course: Course }) {
  return (
    <RevealItem className="h-full">
      <div className="group/card flex h-full flex-col overflow-hidden rounded-[var(--radius-card)] border border-line bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-brand-600/30 hover:shadow-lift">
      <Link href={`/courses/${course.slug}`} className="flex flex-1 flex-col">
        <div className="relative">
          <Cover
            accent={course.accent}
            photo={courseImageFor(course)}
            plain={hasUploadedCourseImage(course)}
            fit="cover"
            className="aspect-[400/250] w-full"
          />
          <div className="absolute left-3 top-3 flex items-center gap-2">
            <span className="rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-700 backdrop-blur">
              {course.category}
            </span>
          </div>
          {course.rating ? (
            <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-ink/55 px-2 py-1 text-[11px] font-semibold text-white backdrop-blur">
              <Star className="h-3 w-3 fill-gold-400 text-gold-400" />
              {course.rating}
            </span>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col p-5">
          <h3 className="font-display text-lg font-semibold leading-snug text-ink transition-colors group-hover/card:text-brand-700">
            {course.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ink-soft">
            {course.description?.[0] || course.summary}
          </p>

          <div className="my-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {course.duration}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" /> {course.level}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Laptop className="h-3.5 w-3.5" /> {course.mode}
            </span>
          </div>

          <div className="mt-auto space-y-1.5 border-t border-line pt-4">
            {course.enrolled ? (
              <p className="flex items-center gap-1.5 text-xs text-ink-muted">
                <Users className="h-3.5 w-3.5 shrink-0" />
                {course.enrolled.toLocaleString("en-IN")} enrolled
              </p>
            ) : course.batchNote ? (
              <p className="flex items-start gap-1.5 text-xs text-ink-muted">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="line-clamp-2">{course.batchNote}</span>
              </p>
            ) : null}
            <p className="text-sm font-semibold text-brand-700">{PAYMENT_NOTE}</p>
          </div>

        </div>
      </Link>
      <div className="flex items-center justify-between gap-3 px-5 pb-5">
        <Link href={`/courses/${course.slug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700">
          View course
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/card:translate-x-1" />
        </Link>
        <EnrollButton course={course} size="sm" />
      </div>
      </div>
    </RevealItem>
  );
}
